import { useState, useEffect, useRef } from 'react';
import { AnnotationCanvas } from '../components/AnnotationCanvas';
import { getIceServers } from '../iceServers';

export function AgentPage({ socket, token, agent, onLogout, sessionCode, onEndSession }) {
  const [step, setStep] = useState('waiting');
  const [supervisorPresent, setSupervisorPresent] = useState(false);
  const [supervisorUsername, setSupervisorUsername] = useState(null);
  const [supervisorMessages, setSupervisorMessages] = useState([]);
  const [supervisorModalOpen, setSupervisorModalOpen] = useState(false);
  const [supervisorUnread, setSupervisorUnread] = useState(0);
  const [supervisorInput, setSupervisorInput] = useState('');
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [editingTemplateText, setEditingTemplateText] = useState('');
  const [newTemplateText, setNewTemplateText] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');
  const [duration, setDuration] = useState(0);
  const [hasStream, setHasStream] = useState(false);
  const videoRef = useRef(null);
  const timerRef = useRef(null);
  const sessionCodeRef = useRef(sessionCode || '');
  const peerRef = useRef(null);
  const streamRef = useRef(null);
  const [clientDimensions, setClientDimensions] = useState(null);
  const iceCandidatesBuffer = useRef([]);

  const [appsPanelOpen, setAppsPanelOpen] = useState(false);
  const [appsLoading, setAppsLoading] = useState(false);
  const [appsList, setAppsList] = useState(null);
  const [appsDenied, setAppsDenied] = useState(false);
  const [appsSearch, setAppsSearch] = useState('');
  const [appsShowSystem, setAppsShowSystem] = useState(false);
  const [appsSortBy, setAppsSortBy] = useState('installDate');

  const [quality, setQuality] = useState(null);
  const [showQualityTooltip, setShowQualityTooltip] = useState(false);
  const qualitySamplesRef = useRef([]);
  const qualityIntervalRef = useRef(null);
  const prevStatsRef = useRef(null);

  const [blackScreenDetected, setBlackScreenDetected] = useState(false);
  const blackScreenCountRef = useRef(0);
  const clientPausedRef = useRef(false);
  const blackScreenIntervalRef = useRef(null);
  const blackScreenCanvasRef = useRef(null);

  const [transferPanelOpen, setTransferPanelOpen] = useState(false);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [transferTarget, setTransferTarget] = useState(null);
  const [transferMessage, setTransferMessage] = useState('');
  const [transferSending, setTransferSending] = useState(false);
  const [transferSentTo, setTransferSentTo] = useState(null);

  const [screenshots, setScreenshots] = useState([]);
  const [screenshotTaking, setScreenshotTaking] = useState(false);
  const [viewingScreenshot, setViewingScreenshot] = useState(null);
  const [screenshotComment, setScreenshotComment] = useState('');

  useEffect(() => {
    sessionCodeRef.current = sessionCode || '';
    setStep('waiting');
    setChatMessages([{ text: `Sesija preuzeta. Kod: ${sessionCode}`, system: true }]);
    // Ako se komponenta ponovo mount-uje (agent se vratio "Vrati se u sesiju"),
    // zatrazi od klijenta nov WebRTC offer jer je stari peerRef izgubljen unmount-om
    if (socket && sessionCode) {
      socket.emit('agent:request-resync', { code: sessionCode, token });
    }
  }, [sessionCode]);

  useEffect(() => {
    if (hasStream && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [hasStream]);

  // ============ KVALITET KONEKCIJE ============

  const classifyQuality = (rtt, loss) => {
    if (rtt == null || loss == null) return 'unknown';
    if (rtt < 100 && loss < 1) return 'excellent';
    if (rtt < 250 && loss < 3) return 'good';
    if (rtt < 500 && loss < 8) return 'poor';
    return 'critical';
  };

  const sampleQuality = async () => {
    if (!peerRef.current) return;
    try {
      const stats = await peerRef.current.getStats();
      let rtt = null;
      let packetsLost = 0;
      let packetsReceived = 0;
      let bytesReceived = 0;
      let now = Date.now();

      stats.forEach(report => {
        if (report.type === 'candidate-pair' && report.state === 'succeeded') {
          if (report.currentRoundTripTime != null) rtt = report.currentRoundTripTime * 1000;
        }
        if (report.type === 'inbound-rtp' && report.kind === 'video') {
          packetsLost = report.packetsLost || 0;
          packetsReceived = report.packetsReceived || 0;
          bytesReceived = report.bytesReceived || 0;
        }
      });

      const lossPct = (packetsReceived + packetsLost) > 0
        ? (packetsLost / (packetsReceived + packetsLost)) * 100 : 0;

      let bitrateKbps = null;
      if (prevStatsRef.current) {
        const dt = (now - prevStatsRef.current.time) / 1000;
        const dBytes = bytesReceived - prevStatsRef.current.bytesReceived;
        if (dt > 0) bitrateKbps = (dBytes * 8) / dt / 1000;
      }
      prevStatsRef.current = { time: now, bytesReceived };

      const level = classifyQuality(rtt, lossPct);
      setQuality({ level, rtt, loss: lossPct, bitrate: bitrateKbps });

      if (rtt != null) {
        qualitySamplesRef.current.push({ rtt, loss: lossPct, bitrate: bitrateKbps });
      }
    } catch (e) {
      console.warn('[Agent] getStats greška:', e);
    }
  };

  useEffect(() => {
    if (step === 'live') {
      qualityIntervalRef.current = setInterval(sampleQuality, 2000);
    } else {
      clearInterval(qualityIntervalRef.current);
      if (step === 'waiting') {
        setQuality(null);
        prevStatsRef.current = null;
      }
    }
    return () => clearInterval(qualityIntervalRef.current);
  }, [step]);

  const sendQualityReport = () => {
    const samples = qualitySamplesRef.current;
    if (samples.length === 0) return;
    const avgRtt = samples.reduce((a, s) => a + s.rtt, 0) / samples.length;
    const avgLoss = samples.reduce((a, s) => a + s.loss, 0) / samples.length;
    const bitrateSamples = samples.filter(s => s.bitrate != null);
    const avgBitrate = bitrateSamples.length
      ? bitrateSamples.reduce((a, s) => a + s.bitrate, 0) / bitrateSamples.length : null;

    socket.emit('agent:report-quality', {
      code: sessionCodeRef.current,
      avgRtt: Math.round(avgRtt),
      avgLoss: Math.round(avgLoss * 10) / 10,
      avgBitrate: avgBitrate != null ? Math.round(avgBitrate) : null
    });
    qualitySamplesRef.current = [];
  };

  // ============ DETEKCIJA CRNOG EKRANA (FLAG_SECURE) ============

  const BLACK_SCREEN_THRESHOLD = 15; // prosecna svetlina ispod ovoga = crno (skala 0-255)
  const BLACK_SCREEN_CONFIRM_COUNT = 2; // koliko uzastopnih merenja pre nego sto se prijavi

  const checkBlackScreen = () => {
    // Klijent je namerno pauzirao deljenje - crn ekran nije zasticena aplikacija
    if (clientPausedRef.current) return;
    const video = videoRef.current;
    if (!video || video.readyState < 2) return;

    if (!blackScreenCanvasRef.current) {
      blackScreenCanvasRef.current = document.createElement('canvas');
      blackScreenCanvasRef.current.width = 32;
      blackScreenCanvasRef.current.height = 32;
    }
    const canvas = blackScreenCanvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    try {
      ctx.drawImage(video, 0, 0, 32, 32);
      const data = ctx.getImageData(0, 0, 32, 32).data;
      let sum = 0;
      const pixelCount = data.length / 4;
      for (let i = 0; i < data.length; i += 4) {
        sum += (data[i] + data[i + 1] + data[i + 2]) / 3;
      }
      const avgBrightness = sum / pixelCount;

      if (avgBrightness < BLACK_SCREEN_THRESHOLD) {
        blackScreenCountRef.current += 1;
        if (blackScreenCountRef.current >= BLACK_SCREEN_CONFIRM_COUNT && !blackScreenDetected) {
          setBlackScreenDetected(true);
          socket?.emit('agent:black-screen-detected', { code: sessionCodeRef.current });
        }
      } else {
        if (blackScreenCountRef.current > 0 && blackScreenDetected) {
          socket?.emit('agent:black-screen-cleared', { code: sessionCodeRef.current });
        }
        blackScreenCountRef.current = 0;
        if (blackScreenDetected) setBlackScreenDetected(false);
      }
    } catch (e) {
      console.warn('[BlackScreen] Greska pri citanju frejma:', e.name, e.message);
    }
  };

  useEffect(() => {
    if (step === 'live') {
      blackScreenIntervalRef.current = setInterval(checkBlackScreen, 2000);
    } else {
      clearInterval(blackScreenIntervalRef.current);
      blackScreenCountRef.current = 0;
      setBlackScreenDetected(false);
    }
    return () => clearInterval(blackScreenIntervalRef.current);
  }, [step, blackScreenDetected, socket]);

  // ============ TRANSFER SESIJE ============

  const openTransferPanel = () => {
    setTransferPanelOpen(true);
    setTransferSentTo(null);
    setTransferTarget(null);
    setTransferMessage('');
    socket.emit('agent:list-available', { token });
  };

  const sendTransferRequest = () => {
    if (!transferTarget) return;
    setTransferSending(true);
    socket.emit('agent:transfer-request', {
      code: sessionCodeRef.current,
      toUsername: transferTarget,
      message: transferMessage.trim(),
      token
    });
    setTransferSentTo(transferTarget);
  };

  // ============ SCREENSHOTS ============

  const SERVER_URL = window.location.origin;

  const loadScreenshots = async () => {
    try {
      const res = await fetch(`${SERVER_URL}/api/screenshots?session=${sessionCodeRef.current}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setScreenshots(await res.json());
    } catch (e) {
      console.warn('[Screenshot] Greška pri učitavanju liste:', e);
    }
  };

  const takeScreenshot = async () => {
    if (!videoRef.current || screenshotTaking) return;
    if (screenshots.length >= 20) {
      alert('Dostignut je limit od 20 snimaka po sesiji.');
      return;
    }
    setScreenshotTaking(true);
    try {
      const video = videoRef.current;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = canvas.toDataURL('image/jpeg', 0.9);

      const res = await fetch(`${SERVER_URL}/api/screenshots`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ sessionCode: sessionCodeRef.current, imageData, comment: '' })
      });
      const body = await res.json();
      if (!res.ok) {
        alert(body.error || 'Greška pri snimanju');
      } else {
        await loadScreenshots();
        socket.emit('chat:message', { code: sessionCodeRef.current, text: '📷 Agent je snimio trenutni ekran', from: 'agent' });
        setChatMessages(prev => [...prev, { text: 'Napravljen je snimak ekrana', system: true }]);
      }
    } catch (e) {
      console.error('[Screenshot] Greška:', e);
      alert('Greška pri snimanju ekrana');
    } finally {
      setScreenshotTaking(false);
    }
  };

  const deleteScreenshot = async (id) => {
    if (!window.confirm('Obrisati ovaj snimak?')) return;
    try {
      const res = await fetch(`${SERVER_URL}/api/screenshots/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setScreenshots(prev => prev.filter(s => s.id !== id));
        setViewingScreenshot(null);
      }
    } catch (e) {
      console.error('[Screenshot] Greška pri brisanju:', e);
    }
  };

  const saveScreenshotComment = async (id) => {
    try {
      await fetch(`${SERVER_URL}/api/screenshots/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ comment: screenshotComment })
      });
      setScreenshots(prev => prev.map(s => s.id === id ? { ...s, comment: screenshotComment } : s));
    } catch (e) {
      console.error('[Screenshot] Greška pri čuvanju komentara:', e);
    }
  };

  useEffect(() => {
    if (sessionCode) loadScreenshots();
  }, [sessionCode]);

  // ============ SOCKET EVENTI ============

  useEffect(() => {
    if (!socket) return;

    socket.on('client:ready', () => {
      console.log('[Agent] Klijent spreman');
    });

    socket.on('client:paused', () => {
      clientPausedRef.current = true;
      setChatMessages(prev => [...prev, { text: 'Klijent je privremeno pauzirao deljenje', system: true }]);
    });

    socket.on('client:resumed', () => {
      clientPausedRef.current = false;
      setChatMessages(prev => [...prev, { text: 'Klijent je nastavio deljenje', system: true }]);
    });

    socket.on('client:dimensions', ({ width, height }) => {
      setClientDimensions({ width, height });
    });

    socket.on('client:sharing', () => {
      setStep('live');
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
      setChatMessages(prev => [...prev, { text: 'Klijent je počeo deljenje ekrana', system: true }]);
    });

    socket.on('client:stopped', () => {
      setStep('waiting');
      sendQualityReport();
      clearInterval(timerRef.current);
      setDuration(0);
      if (videoRef.current) videoRef.current.srcObject = null;
      setHasStream(false);
      streamRef.current = null;
      setChatMessages(prev => [...prev, { text: 'Klijent je zaustavio deljenje', system: true }]);
    });

    socket.on('session:ended', () => {
      sendQualityReport();
      setChatMessages(prev => [...prev, { text: 'Sesija je završena', system: true }]);
      clearInterval(timerRef.current);
      setDuration(0);
      if (videoRef.current) videoRef.current.srcObject = null;
      setHasStream(false);
      streamRef.current = null;
      if (peerRef.current) { peerRef.current.close(); peerRef.current = null; }
      if (onEndSession) onEndSession();
    });

    socket.on('client:disconnected', () => {
      clearInterval(timerRef.current);
      setDuration(0);
      if (videoRef.current) videoRef.current.srcObject = null;
      setHasStream(false);
      setStep('waiting');
      streamRef.current = null;
      if (peerRef.current) { peerRef.current.close(); peerRef.current = null; }
      setChatMessages(prev => [...prev, { text: 'Klijent se diskonektovao (verovatno je zatvorio aplikaciju)', system: true }]);
      setClientDimensions(null);
    });

    socket.on('apps:response', ({ approved, apps }) => {
      setAppsLoading(false);
      if (approved) {
        setAppsList(apps || []);
        setAppsDenied(false);
        setChatMessages(prev => [...prev, { text: 'Klijent je odobrio uvid u listu aplikacija', system: true }]);
      } else {
        setAppsList(null);
        setAppsDenied(true);
        setChatMessages(prev => [...prev, { text: 'Klijent je odbio zahtev za listu aplikacija', system: true }]);
      }
    });

    socket.on('agents:available', (list) => {
      setAvailableAgents(list);
    });

    socket.on('transfer:declined', ({ reason }) => {
      setTransferSending(false);
      setTransferSentTo(null);
      const msg = reason
        ? `Agent je odbio preuzimanje sesije. Razlog: ${reason}`
        : 'Agent je odbio preuzimanje sesije';
      setChatMessages(prev => [...prev, { text: msg, system: true }]);
    });

    socket.on('transfer:completed', () => {
      setTransferSending(false);
      setTransferPanelOpen(false);
      setChatMessages(prev => [...prev, { text: `Sesija prebačena na agenta ${transferTarget}`, system: true }]);
      if (peerRef.current) { peerRef.current.close(); peerRef.current = null; }
      if (onEndSession) onEndSession();
    });

    socket.on('webrtc:offer', async ({ offer }) => {
      console.log('[Agent] Offer primljen, kreiram answer...');
      if (peerRef.current) peerRef.current.close();
      // Dok cekamo ICE listu, kandidati koji stignu idu u buffer (ne na stari, zatvoren peer)
      peerRef.current = null;
      const peer = new RTCPeerConnection({ iceServers: await getIceServers(socket) });
      peerRef.current = peer;

      peer.ontrack = (e) => {
        console.log('[Agent] Stream primljen!', e.streams[0]);
        streamRef.current = e.streams[0];
        setHasStream(true);
        if (videoRef.current) {
          videoRef.current.srcObject = e.streams[0];
          videoRef.current.play().catch(err => console.warn('Play error:', err));
        }
      };
      peer.onicecandidate = (e) => {
        if (e.candidate) {
          socket.emit('webrtc:ice', { code: sessionCodeRef.current, candidate: e.candidate });
        }
      };

      peer.onconnectionstatechange = () => {
        console.log('[Agent] WebRTC state:', peer.connectionState);
      };

      peer.oniceconnectionstatechange = () => {
        console.log('[Agent] ICE state:', peer.iceConnectionState);
      };

      await peer.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await peer.createAnswer();
      await peer.setLocalDescription(answer);

      for (const candidate of iceCandidatesBuffer.current) {
        try {
          await peer.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (e) {
          console.warn('[Agent] Buffered ICE greška:', e);
        }
      }
      iceCandidatesBuffer.current = [];

      socket.emit('webrtc:answer', { code: sessionCodeRef.current, answer });
      console.log('[Agent] Answer poslat!');
    });

    socket.on('webrtc:ice', async ({ candidate }) => {
      if (peerRef.current && peerRef.current.remoteDescription) {
        try {
          await peerRef.current.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (e) {
          console.warn('[Agent] ICE greška:', e);
        }
      } else {
        iceCandidatesBuffer.current.push(candidate);
      }
    });

    socket.on('chat:message', ({ text }) => {
      setChatMessages(prev => [...prev, { text, from: 'client' }]);
    });

    socket.on('supervisor:joined', ({ supervisorUsername: name }) => {
      setSupervisorPresent(true);
      setSupervisorUsername(name);
      setSupervisorMessages(prev => [...prev, { text: `Supervizor ${name} prati ovu sesiju`, system: true }]);
    });

    socket.on('supervisor:left', () => {
      setSupervisorPresent(false);
      setSupervisorMessages(prev => [...prev, { text: 'Supervizor je prestao da prati sesiju', system: true }]);
    });

    socket.on('supervisor:private-message', ({ text, from }) => {
      setSupervisorMessages(prev => [...prev, { text, from }]);
      if (!supervisorModalOpen) setSupervisorUnread(prev => prev + 1);
    });

    return () => {
      socket.off('client:ready');
      socket.off('client:paused');
      socket.off('client:resumed');
      socket.off('supervisor:joined');
      socket.off('supervisor:left');
      socket.off('supervisor:private-message');
      socket.off('client:sharing');
      socket.off('client:stopped');
      socket.off('session:ended');
      socket.off('client:disconnected');
      socket.off('webrtc:offer');
      socket.off('webrtc:ice');
      socket.off('chat:message');
      socket.off('client:dimensions');
      socket.off('apps:response');
      socket.off('agents:available');
      socket.off('transfer:declined');
      socket.off('transfer:completed');
      clearInterval(timerRef.current);
    };
  }, [socket, onEndSession, transferTarget]);

  const endSession = () => {
    sendQualityReport();
    socket.emit('session:end', { code: sessionCodeRef.current });
    if (peerRef.current) peerRef.current.close();
    clearInterval(timerRef.current);
    setDuration(0);
    if (videoRef.current) videoRef.current.srcObject = null;
    setHasStream(false);
    streamRef.current = null;
    setChatMessages([]);
    if (onEndSession) onEndSession();
  };

  const backToList = () => {
    clearInterval(timerRef.current);
    if (onEndSession) onEndSession();
  };

  const sendChat = () => {
    if (!chatInput.trim()) return;
    socket.emit('chat:message', { code: sessionCodeRef.current, text: chatInput, from: 'agent' });
    setChatMessages(prev => [...prev, { text: chatInput, from: 'agent' }]);
    setChatInput('');
  };

  const sendSupervisorReply = () => {
    if (!supervisorInput.trim()) return;
    socket.emit('supervisor:private-message', { code: sessionCodeRef.current, text: supervisorInput, from: 'agent' });
    setSupervisorMessages(prev => [...prev, { text: supervisorInput, from: 'agent' }]);
    setSupervisorInput('');
  };

  const openSupervisorModal = () => {
    setSupervisorModalOpen(true);
    setSupervisorUnread(0);
  };

  // ============ PREDLOSCI PORUKA ============

  const loadTemplates = async () => {
    try {
      const res = await fetch(`${SERVER_URL}/api/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setTemplates(await res.json());
    } catch (e) {
      console.warn('[Predlošci] Greška pri učitavanju:', e);
    }
  };

  const openTemplates = () => {
    setTemplatesOpen(true);
    loadTemplates();
  };

  const useTemplate = (text) => {
    setChatInput(text);
    setTemplatesOpen(false);
  };

  const addTemplate = async () => {
    if (!newTemplateText.trim()) return;
    try {
      const res = await fetch(`${SERVER_URL}/api/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ text: newTemplateText })
      });
      if (res.ok) {
        setNewTemplateText('');
        loadTemplates();
      }
    } catch (e) {
      console.error('[Predlošci] Greška pri dodavanju:', e);
    }
  };

  const startEditTemplate = (t) => {
    setEditingTemplateId(t.id);
    setEditingTemplateText(t.text);
  };

  const saveEditTemplate = async (id) => {
    if (!editingTemplateText.trim()) return;
    try {
      const res = await fetch(`${SERVER_URL}/api/templates/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ text: editingTemplateText })
      });
      if (res.ok) {
        setEditingTemplateId(null);
        loadTemplates();
      }
    } catch (e) {
      console.error('[Predlošci] Greška pri izmeni:', e);
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Obrisati ovaj predložak?')) return;
    try {
      const res = await fetch(`${SERVER_URL}/api/templates/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) loadTemplates();
    } catch (e) {
      console.error('[Predlošci] Greška pri brisanju:', e);
    }
  };

  const requestAppsList = () => {
    setAppsPanelOpen(true);
    setAppsLoading(true);
    setAppsDenied(false);
    setAppsList(null);
    socket.emit('agent:request-apps', { code: sessionCodeRef.current });
  };

  const downloadAppsList = () => {
    if (!appsList || appsList.length === 0) return;
    const header = 'Naziv,Paket,Sistemska,Datum instalacije,Datum azuriranja\n';
    const rows = appsList.map(a => {
      const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
      return [esc(a.name), esc(a.packageName), a.isSystem ? 'Da' : 'Ne', esc(a.firstInstallDate), esc(a.lastUpdateDate)].join(',');
    }).join('\n');
    const csv = header + rows;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `aplikacije_${sessionCode}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatDuration = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const filteredApps = (appsList || [])
    .filter(a => appsShowSystem || !a.isSystem)
    .filter(a => !appsSearch || a.name.toLowerCase().includes(appsSearch.toLowerCase()) || a.packageName.toLowerCase().includes(appsSearch.toLowerCase()))
    .sort((a, b) => {
      if (appsSortBy === 'name') return a.name.localeCompare(b.name);
      return new Date(b.firstInstallDate) - new Date(a.firstInstallDate);
    });

  const qualityColors = {
    excellent: '#1D9E75',
    good: '#8BC34A',
    poor: '#EF9F27',
    critical: '#E24B4A',
    unknown: '#999'
  };

  const qualityBars = {
    excellent: 4,
    good: 3,
    poor: 2,
    critical: 1,
    unknown: 0
  };

  const s = {
    wrap: { display: 'flex', height: '100vh', fontFamily: 'sans-serif', background: '#f0f0f0' },
    main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 },
    topbar: { background: '#fff', borderBottom: '1px solid #e5e5e5', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
    topLeft: { display: 'flex', alignItems: 'center', gap: 10 },
    badge: (live) => ({ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: live ? '#E1F5EE' : '#FAEEDA', color: live ? '#085041' : '#633806' }),
    dot: (live) => ({ width: 6, height: 6, borderRadius: '50%', background: live === 'live' ? '#1D9E75' : live === 'waiting' ? '#EF9F27' : '#aaa', animation: live !== 'idle' ? 'pulse 1.2s ease-in-out infinite' : 'none' }),
    codeBox: { background: '#f5f5f5', borderRadius: 6, padding: '4px 10px', fontSize: 13, fontFamily: 'monospace', letterSpacing: 3, fontWeight: 600 },
    btn: (variant) => ({ padding: '7px 14px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500, background: variant === 'primary' ? '#185FA5' : variant === 'danger' ? '#E24B4A' : '#f0f0f0', color: variant === 'primary' || variant === 'danger' ? '#fff' : '#333' }),
    videoWrap: { flex: 1, position: 'relative', background: '#111', overflow: 'hidden' },
    video: { width: '100%', height: '100%', objectFit: 'contain' },
    placeholder: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#555', gap: 10 },
    liveBadge: { position: 'absolute', top: 10, left: 10, background: '#E24B4A', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 4 },
    qualityIndicator: { position: 'absolute', top: 10, left: 90, background: 'rgba(0,0,0,0.6)', borderRadius: 6, padding: '4px 8px', display: 'flex', alignItems: 'flex-end', gap: 2, cursor: 'pointer', height: 20 },
    qualityBar: (active, color) => ({ width: 3, borderRadius: 1, background: active ? color : 'rgba(255,255,255,0.25)' }),
    qualityTooltip: { position: 'absolute', top: 36, left: 90, background: 'rgba(20,20,20,0.95)', color: '#fff', borderRadius: 8, padding: '10px 12px', fontSize: 12, minWidth: 160, zIndex: 20, lineHeight: 1.6 },
    blackScreenOverlay: { position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.85)', color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 40, zIndex: 15 },
    sidebar: { width: 260, flexShrink: 0, background: '#fff', borderLeft: '1px solid #e5e5e5', display: 'flex', flexDirection: 'column' },
    sideSection: { padding: '12px 14px', borderBottom: '1px solid #e5e5e5' },
    sideTitle: { fontSize: 11, fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 },
    chatWrap: { flex: 1, overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 6 },
    chatMsg: (from) => ({ padding: '7px 10px', borderRadius: 8, fontSize: 12, maxWidth: '90%', background: from === 'agent' ? '#E6F1FB' : from === 'client' ? '#f0f0f0' : '#fafafa', alignSelf: from === 'agent' ? 'flex-end' : 'flex-start', fontStyle: from === undefined ? 'italic' : 'normal', color: from === undefined ? '#999' : (from === 'agent' ? '#0C447C' : '#333') }),
    chatInputRow: { padding: '8px 10px', borderTop: '1px solid #e5e5e5', display: 'flex', gap: 4, boxSizing: 'border-box', maxWidth: '100%' },
    chatInput: { flex: 1, minWidth: 0, padding: '7px 8px', borderRadius: 7, border: '1px solid #ddd', fontSize: 13, outline: 'none', boxSizing: 'border-box' },
    endBtn: { margin: 10, padding: '9px', background: '#FCEBEB', color: '#791F1F', border: '1px solid #F09595', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500 },
    appsBtn: { margin: '10px 10px 0', padding: '9px', background: '#F0F6FC', color: '#185FA5', border: '1px solid #C7DEF0', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500 },
    transferBtn: { margin: '8px 10px 0', padding: '9px', background: '#FAF3E8', color: '#8A5A1F', border: '1px solid #EAD3B0', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500 },
    screenshotBtn: { margin: '8px 10px 0', padding: '9px', background: '#EFFBF6', color: '#0D7A54', border: '1px solid #B8E8D4', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500 },
    screenshotStrip: { display: 'flex', gap: 6, padding: '8px 10px', overflowX: 'auto', background: '#f8f8f8', borderTop: '1px solid #e5e5e5' },
    screenshotThumb: { width: 60, height: 60, borderRadius: 6, overflow: 'hidden', cursor: 'pointer', border: '1px solid #ddd', flexShrink: 0, background: '#000' },
    screenshotThumbImg: { width: '100%', height: '100%', objectFit: 'cover' },
    screenshotModal: { background: '#fff', borderRadius: 12, padding: 16, maxWidth: 700, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
    screenshotFullImg: { maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain', borderRadius: 8, background: '#000' },
    commentInput: { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #ddd', borderRadius: 7, outline: 'none', marginTop: 10, boxSizing: 'border-box' },

    templatesModal: { background: '#fff', borderRadius: 12, padding: 16, width: '90vw', maxWidth: 480, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', overflowX: 'hidden', boxSizing: 'border-box' },
    templatesList: { flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 },
    templateRow: { display: 'flex', alignItems: 'flex-start', gap: 6, padding: '8px 10px', background: '#f7f7f7', borderRadius: 8, minWidth: 0 },
    templateText: { flex: 1, fontSize: 13, color: '#222', cursor: 'pointer', lineHeight: 1.4, wordBreak: 'break-word', minWidth: 0 },
    templateIconBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, padding: '2px 4px', color: '#888' },
    templateEditInput: { flex: 1, padding: '5px 7px', fontSize: 13, border: '1px solid #ddd', borderRadius: 5, outline: 'none' },
    templateAddRow: { display: 'flex', gap: 6, borderTop: '1px solid #eee', paddingTop: 10 },
    templateSearchInput: { width: '100%', padding: '7px 10px', fontSize: 13, border: '1px solid #ddd', borderRadius: 7, outline: 'none', marginBottom: 10, boxSizing: 'border-box' },

    // Bocni panel za apps listu - NIJE modal, deo je flex layout-a
    appsPanel: { width: 380, flexShrink: 0, background: '#fff', borderLeft: '1px solid #e5e5e5', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    appsPanelHeader: { padding: '14px 16px', borderBottom: '1px solid #e5e5e5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    appsPanelTitle: { fontSize: 14, fontWeight: 700, color: '#111' },
    appsPanelBody: { flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', minHeight: 0 },
    modalFilters: { display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' },
    searchInput: { flex: 1, padding: '7px 10px', fontSize: 13, border: '1px solid #ddd', borderRadius: 7, outline: 'none', minWidth: 100 },
    select: { width: '100%', padding: '9px 11px', fontSize: 13, border: '1px solid #ddd', borderRadius: 7, outline: 'none', background: '#fff', boxSizing: 'border-box' },
    checkboxLabel: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#555' },
    appsListWrap: { flex: 1, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8, minHeight: 0 },
    appRow: { padding: '9px 12px', borderBottom: '1px solid #f5f5f5', fontSize: 13 },
    appName: { fontWeight: 500, color: '#222' },
    appPkg: { fontSize: 11, color: '#999', marginTop: 1 },
    appDate: { fontSize: 11, color: '#888', marginTop: 4 },
    closeBtn: { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#999' },

    supervisorBubble: {
      position: 'fixed', bottom: 24, right: 24, width: 56, height: 56, borderRadius: '50%',
      background: '#8A5A1F', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 24, cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.25)', zIndex: 300
    },
    supervisorBadge: {
      position: 'absolute', top: -4, right: -4, minWidth: 20, height: 20, borderRadius: 10,
      background: '#E24B4A', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex',
      alignItems: 'center', justifyContent: 'center', border: '2px solid #fff', padding: '0 4px'
    },
    supervisorModal: { background: '#fff', borderRadius: 12, padding: 16, width: '90vw', maxWidth: 420, maxHeight: '70vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
    supervisorMessages: { flex: 1, overflowY: 'auto', padding: '4px 0', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 100 },
    supervisorMsgBubble: (from) => ({
      maxWidth: '80%', padding: '7px 10px', borderRadius: 8, fontSize: 13,
      alignSelf: from === 'agent' ? 'flex-end' : from === 'supervisor' ? 'flex-start' : 'center',
      background: from === 'agent' ? '#E6F1FB' : from === 'supervisor' ? '#FAF3E8' : 'transparent',
      color: from ? '#222' : '#999', fontStyle: from ? 'normal' : 'italic'
    }),
    supervisorInputRow: { display: 'flex', gap: 6, marginTop: 10 },
    emptyState: { padding: 40, textAlign: 'center', color: '#999', fontSize: 13 },
    downloadBtn: { padding: '8px 10px', background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 500, marginTop: 10 },

    modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 },
    modal: { background: '#fff', borderRadius: 12, padding: 20, width: '100%', maxWidth: 560, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.16)' },
    modalTitle: { fontSize: 16, fontWeight: 700, marginBottom: 12, color: '#111', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    agentOption: (selected) => ({
      padding: '10px 12px', border: selected ? '1.5px solid #185FA5' : '1px solid #e5e5e5',
      background: selected ? '#F0F6FC' : '#fff', borderRadius: 8, marginBottom: 6,
      cursor: 'pointer', fontSize: 13, fontWeight: 500
    }),
    transferTextarea: { width: '100%', padding: '9px 11px', fontSize: 13, border: '1px solid #ddd', borderRadius: 7, outline: 'none', marginTop: 10, minHeight: 60, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }
  };

  return (
    <div style={s.wrap}>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.3}}`}</style>
      <div style={s.main}>
        <div style={s.topbar}>
          <div style={s.topLeft}>
            <button style={{ ...s.btn(), padding: '5px 10px' }} onClick={backToList}>← Lista</button>
            <span style={s.badge(step === 'live')}>
              <span style={s.dot(step)} />
              {step === 'waiting' ? 'Čeka deljenje' : `Live · ${formatDuration(duration)}`}
            </span>
            {sessionCode && <span style={s.codeBox}>{sessionCode}</span>}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {agent && <span style={{ fontSize: 12, color: '#666', marginRight: 8 }}>{agent.fullName || agent.username}</span>}
            <button style={s.btn('danger')} onClick={endSession}>Završi sesiju</button>
          </div>
        </div>
        <div style={s.videoWrap}>
          <video
            ref={videoRef}
            style={{ ...s.video, display: hasStream ? 'block' : 'none' }}
            autoPlay
            playsInline
          />
          {hasStream && (
            <>
              <AnnotationCanvas socket={socket} sessionCode={sessionCode} videoRef={videoRef} clientDimensions={clientDimensions} />
              <div style={s.liveBadge}>● LIVE</div>
              {quality && (
                <div
                  style={s.qualityIndicator}
                  onMouseEnter={() => setShowQualityTooltip(true)}
                  onMouseLeave={() => setShowQualityTooltip(false)}
                >
                  {[1, 2, 3, 4].map(i => (
                    <div
                      key={i}
                      style={{
                        ...s.qualityBar(i <= qualityBars[quality.level], qualityColors[quality.level]),
                        height: 4 + i * 3
                      }}
                    />
                  ))}
                </div>
              )}
              {showQualityTooltip && quality && (
                <div style={s.qualityTooltip}>
                  <div>RTT: {quality.rtt != null ? `${Math.round(quality.rtt)} ms` : '—'}</div>
                  <div>Gubitak paketa: {quality.loss != null ? `${quality.loss.toFixed(1)}%` : '—'}</div>
                  <div>Bitrate: {quality.bitrate != null ? `${Math.round(quality.bitrate)} kbps` : '—'}</div>
                </div>
              )}
              {blackScreenDetected && (
                <div style={s.blackScreenOverlay}>
                  <div style={{ fontSize: 32, marginBottom: 10 }}>🔒</div>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Aplikacija ne dozvoljava deljenje ekrana</div>
                  <div style={{ fontSize: 13, opacity: 0.85 }}>Ovo je sigurnosna zaštita aplikacije (npr. bankarska ili autentifikator aplikacija). Zamolite klijenta da opiše šta vidi ili da izađe iz te aplikacije.</div>
                </div>
              )}
            </>
          )}
          {!hasStream && (
            <div style={s.placeholder}>
              <div style={{ fontSize: 40 }}>📡</div>
              <div style={{ fontSize: 14, color: '#888' }}>Čeka se da klijent podeli ekran...</div>
              <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>Kod: <strong style={{ letterSpacing: 3 }}>{sessionCode}</strong></div>
            </div>
          )}
        </div>
        {screenshots.length > 0 && (
          <div style={s.screenshotStrip}>
            {screenshots.map(shot => (
              <div
                key={shot.id}
                style={s.screenshotThumb}
                onClick={() => { setViewingScreenshot(shot); setScreenshotComment(shot.comment || ''); }}
              >
                <img
                  src={`${SERVER_URL}/api/screenshots/${shot.id}/file?token=${token}`}
                  style={s.screenshotThumbImg}
                  alt="screenshot"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {appsPanelOpen && (
        <div style={s.appsPanel}>
          <div style={s.appsPanelHeader}>
            <span style={s.appsPanelTitle}>Instalirane aplikacije {appsList ? `(${filteredApps.length})` : ''}</span>
            <button style={s.closeBtn} onClick={() => setAppsPanelOpen(false)}>✕</button>
          </div>
          <div style={s.appsPanelBody}>
            {appsLoading && <div style={s.emptyState}>Čeka se odgovor klijenta...</div>}
            {appsDenied && <div style={s.emptyState}>Klijent je odbio zahtev za listu aplikacija.</div>}

            {appsList && !appsLoading && (
              <>
                <div style={s.modalFilters}>
                  <input
                    style={s.searchInput}
                    placeholder="Pretraga..."
                    value={appsSearch}
                    onChange={e => setAppsSearch(e.target.value)}
                  />
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
                  <select style={{ ...s.select, width: 'auto', flex: 1 }} value={appsSortBy} onChange={e => setAppsSortBy(e.target.value)}>
                    <option value="installDate">Datum instalacije</option>
                    <option value="name">Naziv</option>
                  </select>
                </div>
                <label style={{ ...s.checkboxLabel, marginBottom: 10 }}>
                  <input type="checkbox" checked={appsShowSystem} onChange={e => setAppsShowSystem(e.target.checked)} />
                  Prikaži sistemske
                </label>

                <div style={s.appsListWrap}>
                  {filteredApps.length === 0 && <div style={s.emptyState}>Nema rezultata</div>}
                  {filteredApps.map(app => (
                    <div key={app.packageName} style={s.appRow}>
                      <div style={s.appName}>{app.name}</div>
                      <div style={s.appPkg}>{app.packageName}</div>
                      <div style={s.appDate}>
                        Instalirano: {app.firstInstallDate}
                        {app.lastUpdateDate !== app.firstInstallDate && ` · Ažurirano: ${app.lastUpdateDate}`}
                      </div>
                    </div>
                  ))}
                </div>

                <button style={s.downloadBtn} onClick={downloadAppsList}>⬇ Preuzmi listu (CSV)</button>
              </>
            )}
          </div>
        </div>
      )}

      <div style={s.sidebar}>
        <div style={s.sideSection}>
          <div style={s.sideTitle}>Sesija</div>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 3 }}>Kod: <strong style={{ letterSpacing: 2 }}>{sessionCode}</strong></div>
        </div>
        <div style={s.sideSection}>
          <div style={s.sideTitle}>Chat sa klijentom</div>
        </div>
        <div style={s.chatWrap}>
          {chatMessages.map((m, i) => (
            <div key={i} style={s.chatMsg(m.from)}>
              {m.system ? m.text : <><strong>{m.from === 'agent' ? 'Ti' : 'Klijent'}:</strong> {m.text}</>}
            </div>
          ))}
        </div>
        <div style={s.chatInputRow}>
          <button style={{ ...s.btn(), padding: '7px 6px', flexShrink: 0, minWidth: 'auto' }} onClick={openTemplates} title="Predlošci poruka">📋</button>
          <input style={s.chatInput} value={chatInput} onChange={e => setChatInput(e.target.value)} placeholder="Poruka klijentu..." onKeyDown={e => e.key === 'Enter' && sendChat()} />
          <button style={{ ...s.btn('primary'), flexShrink: 0 }} onClick={sendChat}>→</button>
        </div>
        <button style={s.appsBtn} onClick={requestAppsList}>📱 Zatraži listu aplikacija</button>
        <button style={s.screenshotBtn} onClick={takeScreenshot} disabled={screenshotTaking || step !== 'live'}>
          {screenshotTaking ? 'Snimam...' : `📷 Screenshot (${screenshots.length}/20)`}
        </button>
        <button style={s.transferBtn} onClick={openTransferPanel}>↔ Transfer sesije</button>
        <button style={s.endBtn} onClick={endSession}>Završi sesiju</button>
      </div>

      {transferPanelOpen && (
        <div style={s.modalOverlay} onClick={() => !transferSending && setTransferPanelOpen(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>
              <span>Transfer sesije {sessionCode}</span>
              <button style={s.closeBtn} onClick={() => setTransferPanelOpen(false)} disabled={transferSending}>✕</button>
            </div>

            {transferSentTo ? (
              <div style={s.emptyState}>
                Čeka se odgovor agenta {transferSentTo}...
              </div>
            ) : (
              <>
                {availableAgents.length === 0 && (
                  <div style={s.emptyState}>Trenutno nema dostupnih agenata za preuzimanje.</div>
                )}
                {availableAgents.length > 0 && (
                  <select
                    style={s.select}
                    value={transferTarget || ''}
                    onChange={e => setTransferTarget(e.target.value || null)}
                  >
                    <option value="">Izaberi agenta za transfer</option>
                    {availableAgents.map(a => (
                      <option key={a.username} value={a.username}>{a.fullName || a.username}</option>
                    ))}
                  </select>
                )}
                {availableAgents.length > 0 && (
                  <>
                    <textarea
                      style={s.transferTextarea}
                      placeholder="Kratka poruka za agenta (opciono)..."
                      value={transferMessage}
                      onChange={e => setTransferMessage(e.target.value)}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                      <button style={s.btn()} onClick={() => setTransferPanelOpen(false)}>Otkaži</button>
                      <button style={s.btn('primary')} onClick={sendTransferRequest} disabled={!transferTarget}>
                        Pošalji zahtev
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {viewingScreenshot && (
        <div style={s.modalOverlay} onClick={() => setViewingScreenshot(null)}>
          <div style={s.screenshotModal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>
              <span>Snimak ekrana - {new Date(viewingScreenshot.created_at).toLocaleString('sr-RS')}</span>
              <button style={s.closeBtn} onClick={() => setViewingScreenshot(null)}>✕</button>
            </div>
            <img
              src={`${SERVER_URL}/api/screenshots/${viewingScreenshot.id}/file?token=${token}`}
              style={s.screenshotFullImg}
              alt="screenshot"
            />
            <input
              style={s.commentInput}
              placeholder="Dodaj komentar..."
              value={screenshotComment}
              onChange={e => setScreenshotComment(e.target.value)}
              onBlur={() => saveScreenshotComment(viewingScreenshot.id)}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
              <button
                style={{ ...s.btn('danger'), fontSize: 12 }}
                onClick={() => deleteScreenshot(viewingScreenshot.id)}
              >
                Obriši snimak
              </button>
              <a
                href={`${SERVER_URL}/api/screenshots/${viewingScreenshot.id}/file?token=${token}`}
                download={`screenshot_${viewingScreenshot.id}.webp`}
                style={{ ...s.btn('primary'), textDecoration: 'none', display: 'inline-block' }}
              >
                Preuzmi
              </a>
            </div>
          </div>
        </div>
      )}

      {templatesOpen && (
        <div style={s.modalOverlay} onClick={() => setTemplatesOpen(false)}>
          <div style={s.templatesModal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>
              <span>Predlošci poruka</span>
              <button style={s.closeBtn} onClick={() => setTemplatesOpen(false)}>✕</button>
            </div>

            <input
              style={s.templateSearchInput}
              placeholder="Pretraga predložaka..."
              value={templateSearch}
              onChange={e => setTemplateSearch(e.target.value)}
              autoFocus
            />

            <div style={s.templatesList}>
              {templates.filter(t => !templateSearch.trim() || t.text.toLowerCase().includes(templateSearch.trim().toLowerCase())).length === 0 && (
                <div style={s.emptyState}>Nema predložaka.</div>
              )}
              {templates.filter(t => !templateSearch.trim() || t.text.toLowerCase().includes(templateSearch.trim().toLowerCase())).map(t => (
                <div key={t.id} style={s.templateRow}>
                  {editingTemplateId === t.id ? (
                    <>
                      <input
                        style={s.templateEditInput}
                        value={editingTemplateText}
                        onChange={e => setEditingTemplateText(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && saveEditTemplate(t.id)}
                        autoFocus
                      />
                      <button style={s.templateIconBtn} onClick={() => saveEditTemplate(t.id)} title="Sačuvaj">✓</button>
                      <button style={s.templateIconBtn} onClick={() => setEditingTemplateId(null)} title="Otkaži">✕</button>
                    </>
                  ) : (
                    <>
                      <span style={s.templateText} onClick={() => useTemplate(t.text)}>{t.text}</span>
                      <button style={s.templateIconBtn} onClick={() => startEditTemplate(t)} title="Izmeni">✏️</button>
                      <button style={s.templateIconBtn} onClick={() => deleteTemplate(t.id)} title="Obriši">🗑️</button>
                    </>
                  )}
                </div>
              ))}
            </div>

            <div style={s.templateAddRow}>
              <input
                style={s.templateEditInput}
                placeholder="Novi predložak..."
                value={newTemplateText}
                onChange={e => setNewTemplateText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addTemplate()}
              />
              <button style={s.btn('primary')} onClick={addTemplate}>Dodaj</button>
            </div>
          </div>
        </div>
      )}

      {supervisorPresent && !supervisorModalOpen && (
        <div style={s.supervisorBubble} onClick={openSupervisorModal} title={`Supervizor ${supervisorUsername} prati sesiju`}>
          👁️
          {supervisorUnread > 0 && <div style={s.supervisorBadge}>{supervisorUnread}</div>}
        </div>
      )}

      {supervisorModalOpen && (
        <div style={s.modalOverlay} onClick={() => setSupervisorModalOpen(false)}>
          <div style={s.supervisorModal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>
              <span>👁️ Supervizor {supervisorUsername}</span>
              <button style={s.closeBtn} onClick={() => setSupervisorModalOpen(false)}>✕</button>
            </div>
            <div style={s.supervisorMessages}>
              {supervisorMessages.map((m, i) => (
                <div key={i} style={s.supervisorMsgBubble(m.from)}>
                  {m.system ? m.text : <><strong>{m.from === 'agent' ? 'Vi' : 'Supervizor'}:</strong> {m.text}</>}
                </div>
              ))}
            </div>
            <div style={s.supervisorInputRow}>
              <input
                style={s.chatInput}
                value={supervisorInput}
                onChange={e => setSupervisorInput(e.target.value)}
                placeholder="Odgovor supervizoru..."
                onKeyDown={e => e.key === 'Enter' && sendSupervisorReply()}
              />
              <button style={s.btn('primary')} onClick={sendSupervisorReply}>→</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}